import * as React from 'react'

import { useNavigate } from '@tanstack/react-router'
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { t } from 'i18next'
import { Translation, useTranslation } from 'react-i18next'

import { Flex, Text } from '@chakra-ui/react'

import {
  ColumnHeader,
  DataTable,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { useApiKeys } from '~/pages/ApiKeys/hooks/useApiKeys'

import { ApiKeyStatusTag } from './ApiKeyStatusTag'

type ApiKeyRow = {
  name: JSX.Element
  keyPrefix: JSX.Element
  status: JSX.Element
  rest: JSX.Element
}

const columnHelper = createColumnHelper<ApiKeyRow>()
export const apiKeyTableColumns = [
  columnHelper.accessor('name', {
    cell: (info) => info.getValue(),
    header: t('pages.api_keys.label.name'),
    enableSorting: false,
    meta: {
      headerStyle: {
        width: '35%',
      },
      cellStyle: {
        width: '35%',
      },
    },
  }),
  columnHelper.accessor('keyPrefix', {
    cell: (info) => info.getValue(),
    header: t('pages.api_keys.label.keyPrefix'),
    enableSorting: false,
    meta: {
      headerStyle: {
        width: '35%',
      },
      cellStyle: {
        width: '35%',
      },
    },
  }),
  columnHelper.accessor('status', {
    cell: (info) => info.getValue(),
    header: t('pages.api_keys.label.status'),
    enableSorting: false,
  }),
  columnHelper.accessor('rest', {
    cell: (info) => info.getValue(),
    header: '',
    enableSorting: false,
    meta: {
      headerStyle: {
        width: '120px',
      },
      cellStyle: {
        width: '120px',
      },
    },
  }),
]

const DetailButton: React.FC<{ apiKeyId: string }> = ({ apiKeyId }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  return (
    <Flex justifyContent="flex-end">
      <TertiaryButton
        onClick={() =>
          navigate({
            to: '/api_keys/$apiKeyId',
            params: { apiKeyId },
          })
        }
      >
        {t('action.detail')}
      </TertiaryButton>
    </Flex>
  )
}

const ApiKeysTable: React.FC = () => {
  const apiKeys = useApiKeys()
  const data = React.useMemo(() => {
    return apiKeys.map((apiKey) => ({
      name: <Text fontWeight="600">{apiKey.name}</Text>,
      keyPrefix: <Text fontFamily="mono">{apiKey.keyPrefix}</Text>,
      status: <ApiKeyStatusTag status={apiKey.status} />,
      rest: <DetailButton apiKeyId={apiKey.id} />,
    }))
  }, [apiKeys])

  const table = useReactTable({
    columns: apiKeyTableColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <DataTable
      renderHeader={(header): JSX.Element => {
        return (
          <Translation key={header.column.id}>
            {(t) => (
              <ColumnHeader>
                {header.column.id === 'rest'
                  ? ''
                  : t(`pages.api_keys.label.${header.column.id}`)}
              </ColumnHeader>
            )}
          </Translation>
        )
      }}
      table={table}
    />
  )
}

export default React.memo(ApiKeysTable)
